import {
  getCheckinUploadUrl,
  createLandmarkCheckin,
  getLandmarkCheckins,
  createPeakCheckin,
  getPeakCheckins,
  updateCheckin,
  deleteCheckin,
  reportCheckin,
  matchCheckinPhotos,
  finishCheckinBatch,
} from "@atlas/shared";
import type { BulkCheckinMatchResponse, BulkPhotoInput } from "@atlas/shared";
import type {
  CheckinUploadUrlResponse,
  CreateCheckinRequest,
  CreateCheckinResponse,
  LandmarkCheckin,
  LandmarkCheckinsResponse,
} from "@atlas/shared";
import { apiClient } from "../lib/api-client";

// Thin wrappers over the shared landmark check-in fetchers (spec 08 LMK-10/11)
// — same pattern as the other src/api modules, client pre-bound.

export type {
  CheckinUploadUrlResponse,
  CreateCheckinRequest,
  CreateCheckinResponse,
  LandmarkCheckin,
  LandmarkCheckinsResponse,
};

export const fetchCheckinUploadUrl = (contentType: string): Promise<CheckinUploadUrlResponse> =>
  getCheckinUploadUrl(apiClient, contentType);

export const postLandmarkCheckin = (
  slug: string,
  body: CreateCheckinRequest,
): Promise<CreateCheckinResponse> => createLandmarkCheckin(apiClient, slug, body);

/** Summit photo check-in (spec 34). Refused with 422 + a SummitCheckinRefusalCode. */
export const postPeakCheckin = (
  slug: string,
  body: CreateCheckinRequest,
): Promise<CreateCheckinResponse> => createPeakCheckin(apiClient, slug, body);

/** A peak's gallery: summit check-ins plus trig pillar check-ins that collected it. */
export const fetchPeakCheckins = (
  slug: string,
  cursor?: string,
): Promise<LandmarkCheckinsResponse> => getPeakCheckins(apiClient, slug, cursor);

export const fetchLandmarkCheckins = (
  slug: string,
  cursor?: string,
): Promise<LandmarkCheckinsResponse> => getLandmarkCheckins(apiClient, slug, cursor);

export const fetchMyLandmarkCheckins = (slug: string): Promise<LandmarkCheckinsResponse> =>
  getLandmarkCheckins(apiClient, slug, undefined, { mine: true });

/** One public check-in photo picked at random (map inspector cover photo). */
export const fetchRandomLandmarkCheckin = (slug: string): Promise<LandmarkCheckinsResponse> =>
  getLandmarkCheckins(apiClient, slug, undefined, { random: true });

export const patchCheckin = (
  id: string,
  updates: { isPrivate?: boolean; caption?: string | null },
): Promise<{ checkin: LandmarkCheckin }> => updateCheckin(apiClient, id, updates);

export const removeCheckin = (id: string): Promise<{ ok: boolean }> =>
  deleteCheckin(apiClient, id);

export const sendCheckinReport = (id: string, reason?: string): Promise<{ ok: boolean }> =>
  reportCheckin(apiClient, id, reason);

/** Bulk photo check-ins (spec 39): locations + dates only, never the photos. */
export const postCheckinPhotoMatch = (photos: BulkPhotoInput[]): Promise<BulkCheckinMatchResponse> =>
  matchCheckinPhotos(apiClient, photos);

export const postCheckinBatchFinish = (batchId: string): Promise<{ ok: boolean }> =>
  finishCheckinBatch(apiClient, batchId);
